import React, { useEffect, useState } from "react";
import useFleetStore from "../stores/useFleetStore";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";

import { useConfirm } from "../components/ConfirmModal/ConfirmContext";
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { buttonClass } from '../components/ui/Button';
import LogoSpinner from '../components/ui/LogoSpinner';
import { StatTile } from '../components/ui/Panel';
import { Chip, ChipGroup } from '../components/ui/Chip';
import Field from '../components/ui/Field';
// Icons
const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M.54 3.87.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3H14.5a2 2 0 0 1 2 2v1.5H.54Zm-.108 1.5H15.5v8a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2V5.37Zm6.324 1.879a.5.5 0 0 0-.808.394v3.503a.5.5 0 0 0 .808.394l2.645-1.752a.5.5 0 0 0 0-.788L6.756 7.25Z"/>
  </svg>
);

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h9a1.5 1.5 0 0 0 1.5-1.5V8.207l.646.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293zM13 7.207V13.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V7.207l5-5z"/>
  </svg>
);

const DroneProjectAssignment = ({ droneIds, closeModal, onDirtyChange }) => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const drones = useFleetStore((state) => state.drones);
  const [projectName, setProjectName] = useState("");
  const [returnHome, setReturnHome] = useState(false);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // A typed project name or the Return Home choice is work the modal would throw away.
  const dirty = returnHome || projectName.trim() !== "";
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract unique projects from the current fleet
  const uniqueProjects = [...new Set(drones.map((drone) => drone.project_assignment))].filter(Boolean);

  // Handle changes in the project name input.
  const handleProjectChange = (e) => {
    setProjectName(e.target.value);
  };

  // Toggle "Return Home". When checked, disable project input and set projectName to "None".
  const handleReturnHomeToggle = (e) => {
    setReturnHome(e.target.checked);
    if (e.target.checked) {
      setProjectName("None");
    } else {
      setProjectName("");
    }
  };

  // Upload the project assignment to the API.
  const handleUpload = async () => {
    if (droneIds.length === 0) {
      setError("No drone IDs provided.");
      return;
    }
    if (!returnHome && !projectName.trim()) {
      setError("Please enter a project name.");
      return;
    }

    // Confirmation dialog
    const actionText = returnHome
      ? `clear the project assignment from ${droneIds.length} drone${droneIds.length !== 1 ? 's' : ''}`
      : `assign "${projectName.trim()}" to ${droneIds.length} drone${droneIds.length !== 1 ? 's' : ''}`;
    const confirmed = await confirm({ message: `Are you sure you want to ${actionText}?` });
    if (!confirmed) return;

    setError("");
    setIsProcessing(true);
    const finalProjectAssignment = returnHome ? "" : projectName.trim();

    const payload = droneIds.map((id) => ({
      serialNumber: id,
      project_assignment: finalProjectAssignment,
    }));

    console.log("Payload to send:", payload);

    try {
      await api.post(`/fleet/drone-bulk-update`, {
        body: payload,
        onSuccess: (result) => {
          toast.success(result.message || "Project assignments updated successfully!");
          closeModal();
        }
      });
    } catch (err) {
      console.error("Upload failed:", err);
      setError(err.message || "An unexpected error occurred while uploading the data.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col max-h-[85vh]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-outline-variant bg-purple-900/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-purple-500/20 flex items-center justify-center text-purple-400">
            <FolderIcon />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Project Assignment</h1>
            <p className="text-on-surface-variant text-sm">{droneIds.length} drone{droneIds.length !== 1 ? 's' : ''} selected</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          {/* Return Home Toggle */}
          <div
            onClick={() => handleReturnHomeToggle({ target: { checked: !returnHome } })}
            className={`flex items-center gap-3 p-4 rounded border cursor-pointer transition-all duration-200
              ${returnHome
                ? 'bg-green-900/20 border-green-500/50'
                : 'bg-surface-container border-transparent hover:bg-surface-container-high'}`}
          >
            <div className={`w-10 h-10 rounded flex items-center justify-center transition-colors
              ${returnHome ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-on-surface-variant'}`}>
              <HomeIcon />
            </div>
            <div className="flex-1">
              <p className={`font-medium ${returnHome ? 'text-green-400' : 'text-white'}`}>
                Return Home
              </p>
              <p className="text-sm text-on-surface-variant">Clear project assignment from selected drones</p>
            </div>
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all
              ${returnHome
                ? 'bg-green-500 border-green-500'
                : 'border-outline-variant'}`}
            >
              <Appear show={returnHome} className="flex">
                <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                </svg>
              </Appear>
            </div>
          </div>

          {/* Project Name Input */}
          <Reveal show={!returnHome}>
            <div className="space-y-2 p-1">
              <label className="block text-sm font-medium text-on-surface-variant">Project Name</label>
              <Field
                type="text"
                size="lg"
                value={projectName}
                onChange={handleProjectChange}
                placeholder="Enter a project name"
              />

              {/* Existing Projects Quick Select */}
              {uniqueProjects.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs text-on-surface-variant mb-2">Or select existing:</p>
                  <ChipGroup label="Existing projects">
                    {uniqueProjects.slice(0, 6).map((project) => (
                      <Chip
                        key={project}
                        pressed={projectName === project}
                        onClick={() => setProjectName(project)}
                      >
                        {project}
                      </Chip>
                    ))}
                  </ChipGroup>
                </div>
              )}
            </div>
          </Reveal>

          {/* Selected Project Preview */}
          <Reveal show={!!projectName && !returnHome}>
            <StatTile on="panel" label="Assigning to Project" value={projectName} />
          </Reveal>

          <Alert tone="error" show={!!error}>{error}</Alert>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-outline-variant bg-surface-container flex justify-end gap-3">
        <button
          onClick={closeModal}
          className={buttonClass('quiet', 'md')}
        >
          Cancel
        </button>
        <button
          onClick={handleUpload}
          disabled={droneIds.length === 0 || (!returnHome && !projectName.trim()) || isProcessing}
          aria-busy={isProcessing}
          className={buttonClass(returnHome ? 'good' : 'filled', 'md')}
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <LogoSpinner size="inline" />
              Processing...
            </span>
          ) : returnHome ? (
            `Return ${droneIds.length} Drone${droneIds.length !== 1 ? 's' : ''} Home`
          ) : (
            `Assign ${droneIds.length} Drone${droneIds.length !== 1 ? 's' : ''}`
          )}
        </button>
      </div>
    </div>
  );
};

export default DroneProjectAssignment;
